import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Animated, Pressable } from "react-native";
import { Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import { AppIcon } from "../AppIcon";
import Box from "../Box";

type Props = {
	title: string;
	/** Rendered as "N selected" only when greater than zero. */
	selectedCount?: number;
	/** Optional node between the title and the count, e.g. a tappable PremiumChip. */
	accessory?: React.ReactNode;
	expanded: boolean;
	onToggleExpanded: () => void;
};

const CollapsibleSectionHeader = ({
	title,
	selectedCount = 0,
	accessory,
	expanded,
	onToggleExpanded,
}: Props) => {
	const { t } = useTranslation();
	const theme = useAppTheme();
	const rotation = useRef(new Animated.Value(expanded ? 1 : 0)).current;

	useEffect(() => {
		const animation = Animated.timing(rotation, {
			toValue: expanded ? 1 : 0,
			duration: 200,
			useNativeDriver: true,
		});
		animation.start();
		return () => animation.stop();
	}, [expanded, rotation]);

	return (
		<Pressable
			onPress={onToggleExpanded}
			accessibilityRole="button"
			accessibilityLabel={t(
				expanded
					? "common.filters.collapseSection"
					: "common.filters.expandSection",
				{ section: title },
			)}
			accessibilityState={{ expanded }}
		>
			<Box flexDirection="row" alignItems="center" gap={12} paddingVertical={6}>
				<Text
					variant="titleSmall"
					style={{ color: theme.colors.onSurface, flex: 1 }}
				>
					{title}
				</Text>
				{accessory}
				{selectedCount > 0 ? (
					<Text
						variant="labelMedium"
						style={{ color: theme.colors.onSurfaceVariant }}
					>
						{t("common.filters.selectedCount", { count: selectedCount })}
					</Text>
				) : null}
				<Animated.View
					style={{
						transform: [
							{
								rotate: rotation.interpolate({
									inputRange: [0, 1],
									outputRange: ["0deg", "180deg"],
								}),
							},
						],
					}}
				>
					<AppIcon
						name="chevron-down"
						size={24}
						color={theme.colors.onSurface}
					/>
				</Animated.View>
			</Box>
		</Pressable>
	);
};

export default CollapsibleSectionHeader;
